/**
 * Item Library Integration for Garden
 * Maps field names to rates from Item Library API
 * Handles dynamic fields where codes come from user selections
 */

import { ItemLibraryResponse, ItemLibraryItem } from '../api/itemLibrary';
import { GARDEN_REFERENCES } from './gardenReferenceMapping';
import { GARDEN_FIELD_LABELS, GARDEN_FIELD_UNITS } from './gardenFieldMapping';
import { EVALUATE_CODES, EvaluateCode } from './constants';

export interface RateInfo {
  rate: number;
  description: string;
  unit: string;
  resourceCode?: string;
}

/**
 * Garden layer items (topsoil, subsoil capping, no-dig) are m³ rates.
 * Convert to m² by multiplying by the user-entered thickness (already in m).
 * Garden stores thickness directly under the field name in gardenDimensionValues.
 */
const M3_RATE_FIELDS = new Set([
  'thickNoDigLayer',
  'spreadLevelCleanSubsoilCapSiteWon',
  'spreadLevelCleanSubsoilCapImported',
  'spreadLevelTopsoilSiteWon',
  'spreadLevelTopsoilImported',
]);
export const GARDEN_M3_CONVERSION_FIELDS: ReadonlySet<string> = M3_RATE_FIELDS;

function parseThicknessValue(raw: string): number | null {
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : null;
}

function getM3ToM2Multiplier(
  fieldName: string,
  dimensionValues: Record<string, string> | undefined,
): number {
  if (!dimensionValues || !M3_RATE_FIELDS.has(fieldName)) return 1;
  const raw = dimensionValues[fieldName];
  if (!raw) return 1;
  const value = parseThicknessValue(raw);
  if (!value || value <= 0) return 1;
  // value >= 1 → entered as mm (e.g. "150") → convert to metres
  // value < 1  → already metres (e.g. "0.150")
  return value >= 1 ? value / 1000 : value;
}

/**
 * Get rate from item library by resource code
 */
function getRateByCode(
  resourceCode: string,
  resourceCodeMap: Map<string, ItemLibraryItem>,
  fieldName: string,
  fallbackDescription?: string,
): RateInfo {
  const normalizedCode = resourceCode.trim().toUpperCase();
  const item = resourceCodeMap.get(normalizedCode);

  if (item) {
    return {
      rate: item.nettRate || item.baseRate || 0,
      description: item.resourceDescription || fallbackDescription || fieldName,
      unit: item.unit || 'm²',
      resourceCode: normalizedCode,
    };
  }

  // Item not found - use fallback description if provided
  return {
    rate: 0,
    description: fallbackDescription || fieldName,
    unit: 'm²',
    resourceCode: '',
  };
}

/**
 * Create reverse mapping from label to code for EVALUATE_CODES
 */
function createLabelToCodeMapping(): Record<string, string> {
  const labelToCode: Record<string, string> = {};

  Object.entries(EVALUATE_CODES).forEach(([code, info]) => {
    if (info.label) {
      labelToCode[info.label] = code;
    }
  });

  return labelToCode;
}

const LABEL_TO_CODE = createLabelToCodeMapping();

/**
 * Get evaluate code from label
 * @param labelOrCode - Could be either a label ("100mm thick AC 32mm dense bin") or code ("ALP042")
 */
function getCodeFromLabelOrCode(labelOrCode: string): { code: string; description: string } | undefined {
  const trimmed = labelOrCode.trim();

  // Check if it's already a code (starts with ALP or GWALP)
  if (trimmed.startsWith('ALP') || trimmed.startsWith('GWALP')) {
    const evaluateCode = trimmed as EvaluateCode;
    if (EVALUATE_CODES[evaluateCode]) {
      return {
        code: trimmed,
        description: EVALUATE_CODES[evaluateCode].label
      };
    }
  }

  // Otherwise, reverse-lookup the code from the label
  const code = LABEL_TO_CODE[trimmed];
  if (code) {
    return {
      code,
      description: trimmed
    };
  }

  return undefined;
}

/**
 * Transform gardenDimensionValues to semantic field format with code and description
 * @param gardenDimensionValues - Dimension values (semantic field names as keys)
 * @returns Semantic field-based dimension values with code and description
 */
function transformDimensionValuesToSemanticFormat(
  gardenDimensionValues: Record<string, string>
): Record<string, { code: string; description: string }> {
  const semanticDimensions: Record<string, { code: string; description: string }> = {};

  // Garden uses semantic field names directly (no column mapping needed)
  Object.entries(gardenDimensionValues).forEach(([fieldName, value]) => {
    if (value && typeof value === 'string' && value.trim() !== '') {
      const codeInfo = getCodeFromLabelOrCode(value);
      if (codeInfo) {
        semanticDimensions[fieldName] = codeInfo;
      }
    }
  });

  return semanticDimensions;
}

/**
 * Get dynamic code info for a field from transformed dimension values
 * @param fieldName - Semantic field name (e.g., 'thickNoDigLayer')
 * @param semanticDimensions - Transformed dimension values in semantic format
 */
function getDynamicCodeInfo(
  fieldName: string,
  semanticDimensions: Record<string, { code: string; description: string }>
): { code: string; description: string } | undefined {
  return semanticDimensions[fieldName];
}

/**
 * Create a rate lookup map from item library for Garden
 * @param itemLibraryData - Item library response from API
 * @param groundworksData - Groundworks data for dynamic field values (optional)
 */
export function createGardenRateLookupMap(
  itemLibraryData: ItemLibraryResponse | undefined,
  groundworksData?: any,
): Map<string, RateInfo> {
  const rateMap = new Map<string, RateInfo>();

  if (!itemLibraryData) {
    return rateMap;
  }

  // Create a map of resourceCode to item for quick lookup
  const resourceCodeMap = new Map<string, ItemLibraryItem>();
  itemLibraryData.forEach((item: ItemLibraryItem) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      resourceCodeMap.set(code, item);
    }
  });

  // Transform dimension values from semantic format upfront
  const semanticDimensions = groundworksData?.gardenDimensionValues
    ? transformDimensionValuesToSemanticFormat(groundworksData.gardenDimensionValues)
    : {};

  // Map each field name to its rate info (rate, description, unit)
  Object.entries(GARDEN_REFERENCES).forEach(([fieldName, reference]) => {
    // Get proper label from field mapping as fallback
    const fallbackDescription = GARDEN_FIELD_LABELS[fieldName] || fieldName;

    // Check for dynamic code first (from user selections)
    const dynamicCodeInfo = getDynamicCodeInfo(fieldName, semanticDimensions);

    const dimensionValues = groundworksData?.gardenDimensionValues as Record<string, string> | undefined;

    if (dynamicCodeInfo) {
      const rateInfo = getRateByCode(
        dynamicCodeInfo.code,
        resourceCodeMap,
        fieldName,
        dynamicCodeInfo.description
      );
      const m3ToM2 = getM3ToM2Multiplier(fieldName, dimensionValues);
      rateMap.set(fieldName, {
        ...rateInfo,
        rate: rateInfo.rate * m3ToM2,
        unit: fieldName in GARDEN_FIELD_UNITS ? GARDEN_FIELD_UNITS[fieldName] : rateInfo.unit,
      });
      return;
    }

    if (!reference.code) {
      rateMap.set(fieldName, {
        rate: 0,
        description: fallbackDescription,
        unit: GARDEN_FIELD_UNITS[fieldName] ?? '',
      });
      return;
    }

    // Static code from reference mapping
    const rateInfo = getRateByCode(reference.code, resourceCodeMap, fieldName, fallbackDescription);
    const m3ToM2 = getM3ToM2Multiplier(fieldName, dimensionValues);
    rateMap.set(fieldName, {
      ...rateInfo,
      rate: rateInfo.rate * m3ToM2,
      unit: fieldName in GARDEN_FIELD_UNITS ? GARDEN_FIELD_UNITS[fieldName] : rateInfo.unit,
    });
  });

  return rateMap;
}

/**
 * Get rate info for a specific field name
 */
export function getGardenRateInfo(
  fieldName: string,
  rateLookupMap: Map<string, RateInfo>,
): RateInfo {
  return rateLookupMap.get(fieldName) || { rate: 0, description: fieldName, unit: GARDEN_FIELD_UNITS[fieldName] ?? '' };
}

/**
 * Apply rates and descriptions to Garden summary items
 */
export function applyGardenRatesToSummary(
  summaryItems: any[],
  rateLookupMap: Map<string, RateInfo>,
): any[] {
  return summaryItems.map((item) => {
    const rateInfo = getGardenRateInfo(item.fieldName, rateLookupMap);
    const amount = Math.abs(item.quantity * rateInfo.rate);

    return {
      ...item,
      rate: rateInfo.rate,
      description: item.description,
      unit: rateInfo.unit,
      amount,
      resourceCode: rateInfo.resourceCode,
    };
  });
}
